import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';

import { ICONS_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';

import * as Layout from '_components/atomic/layouts/form';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import WhySection from '_components/composed/sections/WhySection';
import RightPartWhyAboutSection from '_components/composed/sections/RightPartWhyAboutSection';
import AboutMissionSection from '_components/composed/sections/AboutMissionSection';
import AboutBannerSection from '_components/composed/sections/AboutBannerSection';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, fontSizes, spaces } = theme;

const StyledAboutPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  .ourProcessingText {
    margin-bottom: ${spaces.large3X}px;

    p {
      font-family: 'Aeonik';
      font-style: normal;
      font-weight: 400;
      font-size: ${fontSizes.large}px;
      line-height: 150%;
      color: ${colors.gray[700]};
    }
  }

  @media ${devices.tablet} {
    display: block;
    margin-top: ${spaces.regular}px;

    .ourProcessingText p {
      font-size: ${fontSizes.regular}px;
      padding-left: ${spaces.regular}px;
      padding-right: ${spaces.regular}px;
    }
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const AboutPage = () => {
  const router = useRouter();
  return (
    <StyledAboutPage>
      <AboutBannerSection
        header="About Trademark Genius"
        imgSrc={`${publicRuntimeConfig.basePath}/images/about.svg`}
        subHeader="Trademark Genius is dedicated to providing top notch service and support to small businesses and entrepreneurs looking to protect their business and brand worldwide."
      />
      <AboutMissionSection />
      <WhySection
        isLeftMobileView
        imageTitle="We are known for providing the highest level of support through each of our services, including:"
        leftImage={`${publicRuntimeConfig.basePath}/images/filing-renewal-why.png`}
        rightPart={<RightPartWhyAboutSection />}
      />
      <Layout.Divider size={spaces.large24X} desktop />
      <WhySection
        title="Our Processing"
        isLeftMobileView
        reverse
        leftImage={`${publicRuntimeConfig.basePath}/images/aboutOurProcessing.svg`}
        rightPart={
          <div className="ourProcessingText">
            <p>
              Trademark Genius is made up of a team of highly trained
              specialists armed with detailed critical industry knowledge that
              is continually expanding. We are a top providing and trusted
              source for our customers, always valuing confidentiality and
              ensuring information is secured.
            </p>
            <p>
              We take pride in our efficient design, processing structure and
              strong focus on next level customer service along the entirety of
              the way. Our services are known for being seamless, user-friendly
              and above all transparent. The quality and close attention to
              detail of our services cannot be matched, as we are constantly
              improving and staying ahead of industry standards.
            </p>
            <p>
              Trademark Genius thanks you for visiting and using our services.
              We look forward to aiding you in every step of the journey towards
              protecting your business and brand.
            </p>
          </div>
        }
      />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistration)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Start Registering Now
          </Button>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle size={686} rotate={-90} left={-63} top={-199} />
      <StyledImageContainer left={-44} top={285}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledImageContainer left={200} top={'calc(51% + 60px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={686} rotate={90} right={-199} top={155} />
      <StyledImageContainer right={-66} top={78}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={686} rotate={-90} left={-232} bottom={155} />
      <StyledImageContainer left={65} bottom={270}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledAboutPage>
  );
};

export default AboutPage;
